import React, { memo, useCallback, useRef } from "react";
import clsx from "clsx";
import {
  Box,
  Button,
  List,
  ListItem,
  Popover,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  LinearProgress,
  Theme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import RoundedInputField from "../roundedInputField";
import { Scrollbars } from "react-custom-scrollbars";
import { OrganizationInterface } from "../../types";
import isEqual from "react-fast-compare";
import GlobalSharedStateClass from "../../util/globalSharedStateClass";
import { useLocation } from "react-router-dom";
import { ORGANIZATIONS_PAGE } from "../../util/constants";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(0.5),
  },
  paper: {
    minWidth: 300,
  },
  button: {
    marginRight: `${theme.spacing(2)} !important`,
  },
  searchWrapper: {
    padding: `${theme.spacing(1.5)} ${theme.spacing(1)}`,
    "& > div": {
      width: "100%",
    },
  },
  infoBox: {
    padding: `0 ${theme.spacing(1.5)} ${theme.spacing(1)}`,
    color: theme.palette.text.secondary,
  },
  sortButton: {
    "& span": {
      display: "flex",
      justifyContent: "space-evenly",
    },
  },
  orderComponent: {
    marginLeft: "auto",
  },
  checkbox: {
    marginLeft: `-${theme.spacing(1)}`,
  },
  selectedText: {
    marginRight: theme.spacing(2),
  },
  progressBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
  },
  listItemIcon: {
    minWidth: 35,
  },
  secondaryText: {
    fontSize: "0.75rem",
  },
  disabledButton: {
    border: "1px solid rgba(255, 255, 255, 0.3) !important",
    color: "rgba(255, 255, 255, 0.3) !important",
  },
}));

type PropTypes = {
  selectedOrganizations: OrganizationInterface[] | undefined;
  open: boolean;
  isLoading: boolean;
  anchorEl: HTMLButtonElement | null;
  searchValue: string;
  sortBy: string;
  organizationData:
    | { data: OrganizationInterface[]; meta: { count: number } }
    | any;
  searchFilter(item: OrganizationInterface): boolean;
  onSelect(id: number | string): void;
  handleClick(event: React.MouseEvent<HTMLButtonElement>): void;
  handleClose(): void;
  onSearchChange(e: React.ChangeEvent<HTMLInputElement>): void;
  onScroll(): void;
  setSortBy(value: string): void;
  sortListByParam(
    a: { name: string; id: number },
    b: { name: string; id: number }
  ): any;
  deselectAllOrganizations(): void;
};

const OrderComponent = memo(
  ({
    sortBy,
    className,
    setSortBy,
  }: {
    sortBy: string;
    className?: string;
    setSortBy(value: string): void;
  }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const classes = useStyles();

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleClickListItem = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
      },
      []
    );

    const onChange = useCallback(
      (value: string) => {
        const Cache = new GlobalSharedStateClass();
        setSortBy(value);
        Cache.setOrganizationSort(value);
        handleClose();
      },
      [handleClose, setSortBy]
    );

    return (
      <>
        <Button
          size={"small"}
          className={clsx(classes.sortButton, className)}
          onClick={handleClickListItem}
        >
          {sortBy} <UnfoldMoreIcon fontSize={"small"} />
        </Button>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem dense disabled divider>
            Sort by
          </MenuItem>
          <MenuItem
            dense
            selected={sortBy === "name"}
            onClick={() => onChange("name")}
          >
            Name
          </MenuItem>
          <MenuItem
            dense
            selected={sortBy === "selected"}
            onClick={() => onChange("selected")}
          >
            Selected
          </MenuItem>
          <MenuItem
            dense
            selected={sortBy === "id"}
            onClick={() => onChange("id")}
          >
            ID
          </MenuItem>
        </Menu>
      </>
    );
  },
  isEqual
);

type ScrollBarListPropTypes = {
  scrollElementRef: any;
  organizationData:
    | { data: OrganizationInterface[]; meta: { count: number } }
    | any;
  selectedOrganizations: OrganizationInterface[] | undefined;
  searchFilter(item: OrganizationInterface): boolean;
  onSelect(id: number | string): void;
  sortListByParam(
    a: { name: string; id: number },
    b: { name: string; id: number }
  ): any;
  onScrollStop(): void;
};

const ScrollBarList = memo(
  ({
    scrollElementRef,
    organizationData,
    selectedOrganizations,
    searchFilter,
    onSelect,
    sortListByParam,
    onScrollStop,
  }: ScrollBarListPropTypes) => {
    const classes = useStyles();

    return (
      <Scrollbars
        ref={scrollElementRef}
        autoHide
        autoHeight
        autoHeightMin={0}
        autoHeightMax={375}
        onScrollStop={onScrollStop}
      >
        <List>
          {organizationData?.data
            ?.filter(searchFilter)
            .sort(sortListByParam)
            .map((item: { id: number; name: string }) => (
              <ListItem
                key={item.id}
                button
                dense
                divider
                onClick={() => onSelect(item.id)}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Checkbox
                    edge="start"
                    checked={
                      !!(
                        selectedOrganizations?.length &&
                        selectedOrganizations.find(
                          ({ id }: { id: number }) => id === item.id
                        )
                      )
                    }
                    tabIndex={-1}
                    color={"primary"}
                    inputProps={{ "aria-labelledby": item.id.toString() }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={item.id.toString()}
                  classes={{
                    secondary: classes.secondaryText,
                  }}
                  primary={item.name}
                  secondary={`ID: ${item.id}`}
                />
              </ListItem>
            ))}
        </List>
      </Scrollbars>
    );
  },
  isEqual
);

const OrganizationSelectView = ({
  selectedOrganizations,
  open,
  isLoading,
  anchorEl,
  searchValue,
  sortBy,
  organizationData,
  searchFilter,
  onSelect,
  handleClick,
  handleClose,
  onSearchChange,
  onScroll,
  setSortBy,
  sortListByParam,
  deselectAllOrganizations,
}: PropTypes) => {
  const location = useLocation();
  const classes = useStyles();
  const scrollElementRef = useRef<HTMLElement | any>(null);

  const onScrollStop = () => {
    const { clientHeight, scrollHeight, scrollTop } =
      scrollElementRef.current.getValues();
    if (
      ((clientHeight + scrollTop) / scrollHeight) * 100 >= 80 &&
      organizationData?.data?.length !== organizationData?.meta?.count
    ) {
      onScroll();
    }
  };

  const renderSelectedOrgInfo = () => {
    if (selectedOrganizations?.length) {
      return (
        <>
          <Tooltip title={"Deselect all"}>
            <Checkbox
              className={classes.checkbox}
              checked={true}
              indeterminate={true}
              color={"primary"}
              onChange={deselectAllOrganizations}
            />
          </Tooltip>
          <Typography variant={"body2"} className={classes.selectedText}>
            Selected {selectedOrganizations?.length} organization
            {selectedOrganizations?.length === 1 ? "" : "s"}
          </Typography>
        </>
      );
    } else {
      return (
        <Typography variant={"body2"} className={classes.selectedText}>
          No selected organizations
        </Typography>
      );
    }
  };

  const renderPopover = () => {
    return (
      <Popover
        id={"simple-popover"}
        open={open}
        classes={{
          root: classes.root,
          paper: classes.paper,
        }}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box className={classes.searchWrapper}>
          <RoundedInputField
            value={searchValue}
            placeholder={"Search organization..."}
            isSearch={true}
            onChange={onSearchChange}
          />
        </Box>
        <Box
          className={classes.infoBox}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {renderSelectedOrgInfo()}
          <OrderComponent
            className={classes.orderComponent}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </Box>
        <Divider />
        <Box position={"relative"}>
          <ScrollBarList
            organizationData={organizationData}
            scrollElementRef={scrollElementRef}
            selectedOrganizations={selectedOrganizations}
            searchFilter={searchFilter}
            sortListByParam={sortListByParam}
            onSelect={onSelect}
            onScrollStop={onScrollStop}
          />
          {isLoading && <LinearProgress className={classes.progressBar} />}
        </Box>
      </Popover>
    );
  };

  return (
    <>
      <Button
        className={classes.button}
        variant={"outlined"}
        disableElevation
        color={"secondary"}
        classes={{
          disabled: classes.disabledButton,
        }}
        disabled={location.pathname.startsWith(ORGANIZATIONS_PAGE)}
        endIcon={<ExpandMoreIcon />}
        onClick={handleClick}
      >
        {selectedOrganizations?.length
          ? `${selectedOrganizations[0].name}${
              selectedOrganizations.length > 1
                ? ` (${selectedOrganizations.length})`
                : ""
            }`
          : "Select organization"}
      </Button>
      {renderPopover()}
    </>
  );
};

export default memo(OrganizationSelectView, isEqual);
